// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

import { useCallback, useRef, useState } from "react";

import { BridgeClient } from "@/bridge/client";
import type { BridgeTransport } from "@/bridge/types";
import type { QueryOutcome } from "@/contracts/outcome";

import { runQuery } from "./orchestrator";
import { initialProgress, type StageId, type StageProgress } from "./stages";

export type QueryPhase = "idle" | "running" | "done";

export interface DeviceQueryState {
  phase: QueryPhase;
  progress: StageProgress[];
  outcome: QueryOutcome | null;
  run: (identifier: string) => void;
  reset: () => void;
}

export function useDeviceQuery(transport: BridgeTransport): DeviceQueryState {
  const clientRef = useRef<BridgeClient>();
  if (!clientRef.current) {
    clientRef.current = new BridgeClient(transport);
  }

  const [phase, setPhase] = useState<QueryPhase>("idle");
  const [progress, setProgress] = useState<StageProgress[]>(initialProgress);
  const [outcome, setOutcome] = useState<QueryOutcome | null>(null);

  const setStage = useCallback(
    (id: StageId, state: StageProgress["state"], detail?: string) => {
      setProgress((prev) =>
        prev.map((s) =>
          s.id === id
            ? { ...s, state, ...(detail !== undefined ? { detail } : {}) }
            : s,
        ),
      );
    },
    [],
  );

  const run = useCallback(
    (identifier: string) => {
      const client = clientRef.current;
      if (!client || phase === "running") return;

      setPhase("running");
      setProgress(initialProgress());
      setOutcome(null);

      void runQuery(client, identifier, {
        onStage: (id, state, detail) => setStage(id, state, detail),
      })
        .then((result) => {
          setOutcome(result);
          setPhase("done");
        })
        .catch((err: unknown) => {
          // The orchestrator maps failures to halt outcomes; a throw here is
          // an unexpected fault. Surface it through the same halt contract.
          setOutcome({
            kind: "halted",
            reason: "bridge_error",
            detail: err instanceof Error ? err.message : String(err),
          });
          setPhase("done");
        });
    },
    [phase, setStage],
  );

  const reset = useCallback(() => {
    setPhase("idle");
    setProgress(initialProgress());
    setOutcome(null);
  }, []);

  return { phase, progress, outcome, run, reset };
}
